import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { useMoodOptions } from "@/hooks/use-mood-options"
import { useTheme } from "@/components/theme-provider"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { NavLink } from "react-router"
import { ArrowLeft, Moon, Sun, Trash, Download, Upload } from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { toast } from "sonner"

export function Settings() {
  const { theme, setTheme } = useTheme()
  const { moodOptions, updateMoodLabel, resetToDefaults } = useMoodOptions();

  const exportData = () => {
    const data = {
      "moodboard-data": localStorage.getItem("moodboard-data"),
      "moodboard-monthly-comments": localStorage.getItem("moodboard-monthly-comments"),
      "moodboard-options": localStorage.getItem("moodboard-options"),
    };

    try {
      const encoded = btoa(encodeURIComponent(JSON.stringify(data)));
      navigator.clipboard.writeText(encoded);
      toast.success("Données copiées dans le presse-papier !");
    } catch (e) {
      console.error("Export failed", e);
      toast.error("Erreur lors de l'export des données");
    }
  };

  const importData = () => {
    const input = prompt("Collez ici vos données exportées :");
    if (!input) return;

    try {
      const decoded = JSON.parse(decodeURIComponent(atob(input)));

      if (decoded["moodboard-data"]) localStorage.setItem("moodboard-data", decoded["moodboard-data"]);
      if (decoded["moodboard-monthly-comments"]) localStorage.setItem("moodboard-monthly-comments", decoded["moodboard-monthly-comments"]);
      if (decoded["moodboard-options"]) localStorage.setItem("moodboard-options", decoded["moodboard-options"]);

      toast.success("Données importées avec succès !");
      window.location.reload();
    } catch (e) {
      console.error("Import failed", e);
      toast.error("Données invalides ou corrompues.");
    }
  };

  const deleteData = () => {
    localStorage.clear();
    window.location.reload();
  }

  return (
    <div className="container mx-auto p-8 relative z-10">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild className="shrink-0">
            <NavLink to="/">
              <ArrowLeft />
            </NavLink>
          </Button>
          <div className="mb-title text-4xl!">Paramètres</div>
        </div>

        <div className="bg-card border rounded-lg p-6 shadow-sm space-y-6 mt-6">
          <div className="space-y-4">
            <Label className="text-base">Apparence</Label>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Choisir le thème de l'application
              </span>
              <Select value={theme} onValueChange={setTheme}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Thème" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">
                    <div className="flex items-center gap-2">
                      <Sun className="size-4" /> Clair
                    </div>
                  </SelectItem>
                  <SelectItem value="dark">
                    <div className="flex items-center gap-2">
                      <Moon className="size-4" /> Sombre
                    </div>
                  </SelectItem>
                  <SelectItem value="system">Système</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="bg-card border rounded-lg p-6 shadow-sm space-y-6 mt-3">
          <div className="flex items-center justify-between">
            <Label className="text-base">Personnalisation des humeurs</Label>
            <Button variant="outline" size="sm" onClick={resetToDefaults}>Rétablir par défaut</Button>
          </div>
          <div className="space-y-3">
            {moodOptions.map((option, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full shrink-0 ${option.color.split(" ")[0]}`} />
                <Input
                  value={option.label}
                  onChange={(e) => updateMoodLabel(index, e.target.value)}
                  className="flex-1"
                />
              </div>
            ))}
          </div>
        </div>


        <div className="bg-card border rounded-lg p-6 shadow-sm space-y-6 mt-3">
          <div className="space-y-4">
            <Label className="text-base">Vos données</Label>

            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={exportData}>
                  <Download className="mr-2 h-4 w-4" /> Exporter
                </Button>
                <Button variant="outline" className="flex-1" onClick={importData}>
                  <Upload className="mr-2 h-4 w-4" /> Importer
                </Button>
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="w-full">
                    <Trash className="mr-2 h-4 w-4" /> Supprimer toutes mes données
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Êtes-vous absolument sûr ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action est irréversible. Elle supprimera définitivement :
                    </AlertDialogDescription>
                    <div className="text-muted-foreground text-sm">
                      <ul className="list-disc list-inside mt-2">
                        <li>Vos humeurs quotidiennes</li>
                        <li>Vos notes quotidiennes et mensuelles</li>
                        <li>La personnalisation des humeurs</li>
                        <li>Le thème sélectionné</li>
                      </ul>
                    </div>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={deleteData} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Supprimer définitivement
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            <div className="text-sm text-muted-foreground">
              Utilisez l'export pour sauvegarder vos données ou les transférer vers un autre appareil.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}